import { SimiulatorEquipment, SimulatorNozzle } from "@/types/equipment"
import { Cell, EdgeOptions, Graph, NodeOptions } from "@antv/xflow"
import { v4 as uuidv4 } from "uuid";
import { LineJumps, LineType, PortTypes } from "../constant"
import { LineJumpData, LineJumpState, LineJumpType, LineRecord, NodeRecord, NodeShapes, NodeState, NodeType, NozzleRecord, StreamConnection, SvgNozzleLayoutRecord, SvgStreamDataState, UnitTagState } from "../services/modal"
import { nodeRecordToState } from "../services/recordToState"
import { InletPortStrokeColor, OutletPortStrokeColor, nodePortGroups } from "./ports"
import { EquipmentType, Flip, NozzleDirection } from "@/types/common"
import { tagGenerator } from "../services/TagGenerator"
import { cellToNodeState, edgeToLineRecord } from "../services/storage"
import { Edge, EdgeView, Point, PointLike } from "@antv/x6"
import { SvgStreamDirection } from "./SvgStream"
import { mm2px } from "@/utils/size"

function equipmentNozzleToNozzleState(nozzle: SimulatorNozzle): NozzleRecord {
  const {shape, customLayout, name} = nozzle

  return {
    id: uuidv4(),
    name,
    direction: nozzle.direction,
    shapeId: shape?.id,
    image: shape?.svg,
    type: nozzle.type,
    group: shape?.svg ? PortTypes.SvgNozzlePort : PortTypes.NozzlePort,
    layout: customLayout
  }
}

function equipmnetToNode(equipment: SimiulatorEquipment): NodeState {
  const {id, name, nozzles, width, height, image, type, nozzleEdges, customNozzles } = equipment
  const nozzleParams = nozzles.map(item => {
    return equipmentNozzleToNozzleState(item)
  })
  const tag = tagGenerator.generateTag(type);
  return {
    id: uuidv4(),
    tag,
    nodeType: NodeType.SvgNode,
    size: {
      width: mm2px(width),
      height: mm2px(height),
    },
    nozzles: nozzleParams,
    image,
    equipment: {
      id,
      name,
      type,
      nozzleTemps: customNozzles
    },
    nozzleEdges,
    customNozzles: []
  }
}

export const createEdgeOptionFromRecord = (line: LineRecord): EdgeOptions => {
  const {id, tag, showTag = true, hide} = line
  const attrs: any = {
    line: {
      stroke: '#222',
      strokeWidth: 2,
      targetMarker: null,
    },
  }
  if (hide) {
    attrs.path = {
      stroke: 'transparent'
    }
  }
  return {
    id,
    source: line.source,
    vertices: line.vertices,
    target: line.target,
    zIndex: 2,
    data: {type: line.type, tag: tag, showTag, streamId: line.streamId},
    shape: 'edge',
    attrs: attrs,
  }
}

function translatePortByFlip(layout: SvgNozzleLayoutRecord | undefined, flip: Flip | undefined) {
  if (!layout) return layout
  const {x, y} = layout
  if (flip === Flip.X) {
    
    return {...layout, y: 1 - y}
  }

  if (flip === Flip.Y) {
    return {...layout, x: 1 - x}
  }
  return layout;
}

export const createNodeOptionsFromState = (node: NodeState) => {
  const {nozzles, size, id, position, angle, flip, customNozzles = [], lineNozzles = []} = node
  const {width, height} = size
  const autoNozzles = nozzles.map(item => {
    if (item.group !== PortTypes.SvgNozzlePort) {
      return {
        id: item.id,
        group: PortTypes.NozzlePort,
        args: { ...translatePortByFlip(item.layout, flip) },
        data: item,
        attrs: {circle: {
          stroke: item.direction === NozzleDirection.Inlet ? InletPortStrokeColor : OutletPortStrokeColor,
        }}
      }
    }
    
    return {
      id: item.id,
      group: PortTypes.SvgNozzlePort,
      args: { ...translatePortByFlip(item.layout, flip) },
      data: item
    }
  })
  const customPorts = customNozzles.map(item => {
    return {
      id: item.id,
      group: PortTypes.NozzlePort,
      args: { ...translatePortByFlip(item.layout, flip) },
      data: item
    }
  })

  const linePorts = lineNozzles.map(item => {
    return {
      id: item.id,
      group: PortTypes.LinePort,
      args: { ...translatePortByFlip(item.layout, flip) },
      data: item
    }
  })
  const nodeOptions: NodeOptions = {
    id,
    shape: 'svgNode',
    angle: angle ?? 0,
    size: {width, height},
    data: node,
    ports: {
      groups: nodePortGroups,
      items: [...autoNozzles, ...customPorts, ...linePorts]
    },
    zIndex: 1
  }
  if (position) {
    nodeOptions.position = position
  }

  return nodeOptions
}


export const createSvgNodeOption = (equipment: SimiulatorEquipment): NodeOptions => {
  const flowNode = equipmnetToNode(equipment)
  return  createNodeOptionsFromState(flowNode)
}

export const createNodeOptionsFromRecord = (node: NodeRecord): NodeOptions => {
  return createNodeOptionsFromState(nodeRecordToState(node))
}

export const createNodeOptionsFromNodeRecord= (node: NodeRecord): NodeOptions => {
  const nodeState = nodeRecordToState(node)
  return createNodeOptionsFromState(nodeState)
}

export const createBaseStream = (source: PositionLike, target: PositionLike, vertices: PointLike[] = [], dataOptions: any = {}) => {
  const data: any = {...dataOptions, type: LineType.FlowLine}
  if (!dataOptions.tag) {
    const tag = tagGenerator.generateTag(EquipmentType.Stream);
    data.tag = tag;
  }
  return {
    id: uuidv4(),
    source,
    target,
    data: data,
    shape: 'edge',
    vertices,
    attrs: {
      line: {
        stroke: '#222',
        strokeWidth: 2,
        targetMarker: null,
        pointerEvents: 'none',
      },
    },
  }
}

export const createBasicLine = (source: PositionLike, target: PositionLike, vertices = []) => {
  return {
    id: uuidv4(),
    source,
    target,
    data: {type: LineType.Line},
    shape: 'edge',
    vertices,
    attrs: {
      line: {
        stroke: '#222',
        strokeWidth: 2,
        targetMarker: null,
        pointerEvents: 'none',
      },
    },
  }
}

const SvgStreamHeight = mm2px(10);
const SvgStreamWidth = mm2px(20);

export const createSvgStream = (edge: Edge, type: 'source' | 'target', direction: SvgStreamDirection, data: SvgStreamDataState): StreamConnection => {
  const source = edge.getSource() as PointLike;
  const target = edge.getTarget() as PointLike

  const point = type === 'source' ? source : target
  const nodePos = {...point, y: point.y - SvgStreamHeight / 2 }
  if (direction === SvgStreamDirection.In) {
    nodePos.x = point.x - SvgStreamWidth
  }

  const nozzleLayout = {
    y: 0.5,
    x: direction === SvgStreamDirection.Out ? 0 : 1
  }

  const nozzle = {
    id: uuidv4(),
    layout: nozzleLayout,
    group: PortTypes.NozzlePort,
    data: {
      direction: direction === SvgStreamDirection.Out ? NozzleDirection.Inlet : NozzleDirection.Outlet,
      layout: nozzleLayout
    },
    args: {...nozzleLayout}
  }
  
  return {
    id: uuidv4(),
    shape: NodeShapes.SvgStream,
    position: nodePos,
    size: {
      width: SvgStreamWidth,
      height: SvgStreamHeight,
    },
    data,
    ports: {
      groups: nodePortGroups,
      items: [nozzle]
    }
  }
}

export const createTargetSvgStream = (direction: SvgStreamDirection, data: SvgStreamDataState): StreamConnection => {

  const nodePos = {x: 0, y: 0 }

  const nozzleLayout = {
    y: 0.5,
    x: direction === SvgStreamDirection.Out ? 0 : 1
  }

  const nozzle = {
    id: uuidv4(),
    layout: nozzleLayout,
    group: PortTypes.NozzlePort,
    data: {direction: direction === SvgStreamDirection.Out ? NozzleDirection.Inlet : NozzleDirection.Outlet},
    args: {...nozzleLayout}
  }
  
  return {
    id: uuidv4(),
    shape: NodeShapes.SvgStream,
    position: nodePos,
    size: {
      width: SvgStreamWidth,
      height: SvgStreamHeight,
    },
    data,
    ports: {
      groups: nodePortGroups,
      items: [nozzle]
    }
  }
}
const TagHeight = 24
const TagWidth = 90

export const createUnitTagFromRecord = (node: NodeState): NodeOptions => {
  const {size, position} = node
  const {width, height} = size
  let tagOption: UnitTagState | undefined = node.tagOption
  let offsetX
  let offsetY 
  if (!tagOption) {
    offsetX = (width - TagWidth) / 2;
    offsetY = height + 5;
    const offset = {
      x: offsetX,
      y: offsetY,
    }
    tagOption = {
      unit: node.id,
      size: {
        width: TagWidth,
        height: TagHeight,
      },
      text: node.tag,
      offset: offset,
      angle: node.angle,
    }
  } else {
    const {x, y} = tagOption.offset
    offsetX = x
    offsetY = y
  }
  
  return {
    id: getTagIdByNodeId(node.id),
    shape: 'UnitTagNode',
    size: tagOption.size,
    position: {
      x: position!.x + offsetX,
      y: position!.y + offsetY,
    },
    data: {...tagOption, text: node.tag, unit: node.id},
    angle: tagOption.angle
  }
}

export const createUnitTag = (node: NodeState): NodeOptions => {
  const {size, position} = node
  const {width, height} = size
  let tagOption: UnitTagState | undefined = node.tagOption
  if (!tagOption) {
    const offset = {
      x: (width - TagWidth) / 2,
      y: height + 5,
    }
    tagOption = {
      unit: node.id,
      size: {
        width: TagWidth,
        height: TagHeight,
      },
      text: node.tag,
      offset: offset,
      angle: node.angle,
    }
  }
  
  return {
    id: getTagIdByNodeId(node.id),
    shape: 'UnitTagNode',
    size: tagOption.size,
    position: {
      x: position!.x + tagOption.offset.x,
      y: position!.y + tagOption.offset.y,
    },
    data: {...tagOption, text: node.tag, unit: node.id},
    angle: tagOption.angle
  }
}

export const createUnitTagFromEdge = (edge: LineRecord, center?: PositionLike | Point): NodeOptions => {
  let tagOption: UnitTagState | undefined = edge.tagOption
  if (!tagOption) {
    const offset = {
      x: -(TagWidth / 2),
      y: 5,
    }
    tagOption = {
      unit: edge.id,
      size: {
        width: TagWidth,
        height: TagHeight,
      },
      text: edge.tag,
      offset: offset,
      angle: 0,
    }
  }
  
  return {
    id: getTagIdByNodeId(edge.id),
    shape: 'UnitTagNode',
    size: tagOption.size,
    position: {
      x: center!.x + tagOption.offset.x,
      y: center!.y + tagOption.offset.y,
    },
    data: {...tagOption, text: edge.tag, unit: edge.id},
    angle: tagOption.angle
  }
}

export function getTagNodeOptions(cell: Cell, graph: Graph) {
  if (graph.isEdge(cell)) {
    const view = <EdgeView>cell.findView(graph)
    const center = view.getPointAtRatio(0.5)
    return createUnitTagFromEdge(edgeToLineRecord(cell.toJSON()), center!)
  }
  
  return createUnitTag(cellToNodeState(cell.toJSON()))
}

export const getTagIdByNodeId = (nodeId: string) => {
  return `${nodeId}-tag`
}

export const getNodeIdByTagId = (tagId: string) => {
  const strs = tagId.split('-')
  strs.pop();
  return strs.join('-')
}

export function createTextNode(x: number, y: number) {
  return {
    id: uuidv4(),
    size: {width: 100, height: 50},
    shape: NodeShapes.TextNode,
    position: {x, y},
    data: {text: 'Input text...'},
    angle: 0
  }
}

export function createImageNode(x: number, y: number) {
  return {
    id: uuidv4(),
    size: {width: 100, height: 50},
    shape: NodeShapes.ImageNode,
    position: {x, y},
    data: {url: ''},
    angle: 0
    
  }
}

export function createLineJump(position: PointLike, type: LineJumpType, angle = 0, data: Partial<LineJumpData> = {}) {
  const temp = LineJumps[type]
  if (!temp) throw new Error("Type is not valid");
  const {width, height} = temp.size;
  return {
    id: uuidv4(),
    size: temp.size,
    shape: NodeShapes.LineJump,
    position: {x: position.x - width / 2, y: position.y - height / 2},
    data: {image: temp.svg, type, ...data},
    angle,
    ports: {
      groups: nodePortGroups,
      items: [
        {id: uuidv4(), args: temp.start, group: PortTypes.LineJumpPort},
        {id: uuidv4(), args: temp.end, group: PortTypes.LineJumpPort}
      ]
    }
  }
}

export const jumpStateToNodeOptions = (jump: LineJumpState) => {
  const {id, size, angle, position, data, ports, zIndex } = jump;

  const portItems = ports.map(item => {
    return {
      id: item.id,
      args: item.position as {x: number, y: number},
      group: PortTypes.LineJumpPort
    }
  })
  
  return {
    id,
    shape: NodeShapes.LineJump,
    size: size,
    position: position,
    data: data,
    angle: angle,
    ports: {
      groups: nodePortGroups,
      items: portItems
    },
    zIndex: zIndex || 100
  }
}